import type { AppState } from '../domain/state/AppState';
import type { GameRunStats } from '../domain/gameplay/GameRunStats';
import type { GlobalBestState } from '../features/leaderboard/models/global-best';

export type GameEventMap = {
  'app:state-changed': { from: AppState; to: AppState };
  'game:score-changed': { score: number; highScore: number };
  'game:lives-changed': { lives: number; maxLives: number };
  'player:shot': { ammo: number; maxAmmo: number };
  'player:ammo-empty': { ammo: 0; maxAmmo: number };
  'player:ammo-changed': { ammo: number; maxAmmo: number; reason: 'shot' | 'pickup' | 'reset' };
  'player:lives-changed': {
    lives: number;
    maxLives: number;
    reason: 'damage' | 'pickup' | 'reset';
  };
  'powerup:spawned': { type: 'ammo' | 'life' };
  'powerup:collected': { type: 'ammo' | 'life'; value: number };
  'powerup:despawned': { type: 'ammo' | 'life' };
  'ui:feedback': { message: string; tone: 'info' | 'warning' | 'success' };
  'game:over': { score: number; highScore: number; run: GameRunStats };
  'game:ready': undefined;
  'audio:mute-changed': { muted: boolean };
  'intro:progress': { progress: number };
  'leaderboard:updated': undefined;
  'leaderboard:score-saved': { score: number };
  'leaderboard:global-best-changed': GlobalBestState;
};

type Handler<T> = (payload: T) => void;

export class EventBus {
  private readonly handlers = new Map<keyof GameEventMap, Set<Handler<never>>>();

  public on<K extends keyof GameEventMap>(event: K, handler: Handler<GameEventMap[K]>): () => void {
    const group = this.handlers.get(event) ?? new Set<Handler<never>>();
    group.add(handler as Handler<never>);
    this.handlers.set(event, group);
    return () => group.delete(handler as Handler<never>);
  }

  public emit<K extends keyof GameEventMap>(event: K, payload: GameEventMap[K]): void {
    this.handlers.get(event)?.forEach((handler) => handler(payload as never));
  }

  public clear(): void {
    this.handlers.clear();
  }
}
